import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import CheckInModal from '../components/CheckInModal';
import Wordmark from '../components/Wordmark';
import ActionLink from '../components/ActionLink';
import { tokens, getPulseMessage } from '../tokens';
import useIsDesktop from '../useIsDesktop';
import api from '../api';

function PulseCard({ pulseMessage }) {
  return (
    <div style={{
      padding: '22px 24px 26px',
      border: `1px solid ${tokens.edge}`,
      background: `linear-gradient(180deg, ${tokens.wing}, ${tokens.ground})`,
      animation: `gl-fade-up 600ms ${tokens.ease}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
        fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.2em',
        textTransform: 'uppercase', color: tokens.textLow,
      }}>
        <span style={{
          width: 5, height: 5, borderRadius: '50%', background: tokens.accent,
          boxShadow: `0 0 6px ${tokens.accent}`,
          animation: 'gl-breathe 3.5s ease-in-out infinite',
        }} />
        Pulse &middot; this week
      </div>
      <p style={{
        fontFamily: tokens.fontDisplay,
        fontSize: 24, lineHeight: 1.3, fontWeight: tokens.displayWeight,
        letterSpacing: tokens.displayTracking, color: tokens.textHi,
        margin: 0, textWrap: 'pretty',
      }}>{pulseMessage}</p>
      <p style={{
        fontFamily: tokens.fontDisplay, fontStyle: 'italic', fontSize: 14,
        color: tokens.textMid, margin: '12px 0 0', lineHeight: 1.5,
      }}>You're not the only one still standing in the dark.</p>
    </div>
  );
}

function DActionLink({ onClick, children }) {
  return (
    <button className="gl-btn-bare" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      width: '100%', padding: '18px 0',
      borderBottom: `1px solid ${tokens.edge}`,
      fontFamily: tokens.fontDisplay, fontSize: 20,
      letterSpacing: tokens.displayTracking, color: tokens.textHi,
      transition: `color 200ms ${tokens.ease}`,
    }}
    onMouseEnter={e => { e.currentTarget.style.color = tokens.accent; }}
    onMouseLeave={e => { e.currentTarget.style.color = tokens.textHi; }}
    >
      <span>{children}</span>
      <span style={{ fontFamily: tokens.fontMono, fontSize: 16, color: tokens.textLow }}>→</span>
    </button>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [pulse, setPulse] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/checkins/pulse').catch(() => ({ data: null })),
      api.get('/posts', { params: { limit: 3 } }).catch(() => ({ data: { posts: [] } })),
      api.get('/ghost-hours/matches').catch(() => ({ data: { matches: [] } })),
    ]).then(([pulseRes]) => {
      setPulse(pulseRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleCheckedIn = (newPulse) => {
    if (newPulse) setPulse(newPulse);
  };

  const now = new Date();
  const dayStr = now.toLocaleDateString([], { weekday: 'long' });
  const timeStr = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  const pulseMessage = getPulseMessage(pulse);

  /* ── Desktop: Option D — two-column centered composition ── */
  if (isDesktop) {
    return (
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column',
        background: tokens.ground, color: tokens.textHi,
        fontFamily: tokens.fontBody, position: 'relative',
      }}>
        <div className="gl-atmosphere" />
        <Navbar hideTopBar />

        <main style={{
          flex: 1, position: 'relative', zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 40, overflow: 'auto',
        }}>
          {!loading && (
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80,
              maxWidth: 960, width: '100%', alignItems: 'start',
            }}>
              <PulseCard pulseMessage={pulseMessage} />

              <div style={{ animation: `gl-fade-up 600ms ${tokens.ease} 120ms both` }}>
                <div style={{
                  fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.22em',
                  textTransform: 'uppercase', color: tokens.accent, marginBottom: 14,
                }}>Where to go</div>
                <DActionLink onClick={() => navigate('/stage')}>Step onto the stage</DActionLink>
                <DActionLink onClick={() => navigate('/ghost-hours')}>Talk to someone who gets it</DActionLink>
                <DActionLink onClick={() => navigate('/green-room')}>Open a journal page</DActionLink>
              </div>
            </div>
          )}
        </main>

        <CheckInModal
          open={checkInOpen}
          onClose={() => setCheckInOpen(false)}
          onCheckedIn={handleCheckedIn}
        />
      </div>
    );
  }

  /* ── Mobile: single-column ── */
  return (
    <div style={{ position: 'relative', minHeight: '100%', paddingBottom: 120 }}>
      <Navbar hideTopBar />
      <div className="gl-atmosphere" />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 22px' }}>
      {/* Custom top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 15,
        padding: '14px 0 12px',
        background: `linear-gradient(to bottom, ${tokens.ground} 85%, transparent)`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Wordmark size={11} amber />
        <div style={{
          fontFamily: tokens.fontMono, fontSize: 10,
          letterSpacing: '0.14em', color: tokens.textLow,
          textTransform: 'uppercase',
        }}>
          {dayStr} &middot; {timeStr}
        </div>
      </div>

      {/* Pulse card */}
      {!loading && (
        <div style={{ padding: '18px 0 0', position: 'relative', zIndex: 2 }}>
          <PulseCard pulseMessage={pulseMessage} />
        </div>
      )}

      {/* Where to go */}
      {!loading && (
        <div style={{ padding: '40px 0 0', position: 'relative', zIndex: 2 }}>
          <div style={{
            fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: tokens.accent, marginBottom: 18,
          }}>Where to go</div>
          <ActionLink onClick={() => navigate('/stage')}>Step onto the stage</ActionLink>
          <ActionLink onClick={() => navigate('/ghost-hours')}>Talk to someone who gets it</ActionLink>
          <ActionLink onClick={() => navigate('/green-room')}>Open a journal page</ActionLink>
        </div>
      )}
      </div>{/* end max-width wrapper */}

      <CheckInModal
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        onCheckedIn={handleCheckedIn}
      />
    </div>
  );
}
